/* The prototype's data layer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO DATABASE BEHIND THIS. Everything lives in this browser tab's
 * localStorage. Two people demoing at the same time each get their own private
 * sandbox and cannot see each other's tickets. Clearing site data, or the Reset
 * button in the demo bar, puts it back to the seed.
 *
 * WHY it exists: the ticket tables are the expensive thing to get wrong, so the
 * flow gets walked through with real IT staff before any DDL is written.
 *
 * HOW IT GETS REPLACED: every export below is async and shaped like the HTTP
 * call that will replace it — `createTicket(input)` becomes
 * `POST /api/tickets`, `listTickets(filter)` becomes `GET /api/tickets`, and so
 * on. When the tables exist, this file is rewritten to `fetch()` and nothing
 * above it changes. That is the only reason it is async: a synchronous
 * prototype store would let loading states quietly never get built.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  IT_STAFF,
  PRIORITY_RULES,
  SLA_POLICY,
  WORK_CALENDAR,
  typeById,
  categoryById,
} from './catalog.js';
import { evaluate, factsFor } from './rules.js';
import { assertLiveWritesAllowed, guardedFetch, isWalkthroughBuild } from '../lib/liveWrites.js';
import { markSeen } from '../lib/unread.js';
import { filesForReal } from '../lib/filesForReal.js';

// Bump this whenever the seed or the ticket shape changes, so anyone mid-demo
// gets the new data instead of a half-migrated old one. Cheaper and more honest
// than writing migrations for a prototype.
const STORAGE_KEY = 'ihs-it-help.demo.v4';
const FIRST_TICKET_NUMBER = 501; // the live Asana board is at TICK-497

// ------------------------------------------------------------------ profiles

// In the real app these come from Entra groups plus the HR data already in
// Snowflake. Two are offered in the demo bar purely so the "exec or GM" rule
// can be shown firing and not firing.
export const DEMO_PROFILES = {
  dispatcher: {
    id: 'dispatcher',
    label: 'Dispatcher',
    department: 'Dispatch',
    role: 'Dispatcher',
    brand: 'Beehive Plumbing',
    isLeadership: false,
  },
  // Added 2026-08-21 for the technician quick links. `isField` is what Home
  // reshapes on. In the real app it comes from HR data or a ServiceTitan job
  // title, NOT from a switcher — same as every other fact in this object.
  tech: {
    id: 'tech',
    label: 'Service Technician',
    department: 'Field Service',
    role: 'Service Technician',
    brand: 'Beehive Plumbing',
    isLeadership: false,
    isField: true,
  },
  gm: {
    id: 'gm',
    label: 'General Manager',
    department: 'Operations',
    role: 'General Manager',
    brand: 'Beehive Plumbing',
    isLeadership: true,
  },
};

// ---------------------------------------------------------------- seed data

const MIN = 60_000;
const HOUR = 60 * MIN;
/* The demo's reopen window. The real one is the admin's setting (5 days by default), counted by
 * the server as N x 24 hours from the resolve; the demo counts the same way from its own default.
 * The seed's resolved ticket (TICK-505) is stamped when the demo is first loaded, so in a browser
 * that keeps its demo for more than about four days it passes the window: Reset brings it back. */
const DEMO_REOPEN_DAYS = 5;
const demoReopenUntil = (t) => (t.status === 'resolved' && t.resolvedAt
  ? new Date(new Date(t.resolvedAt).getTime() + DEMO_REOPEN_DAYS * 24 * HOUR).toISOString()
  : null);

function ago(ms) {
  return new Date(Date.now() - ms).toISOString();
}

// Seeded so the queue has something to triage on first open, covering every
// state worth demonstrating: an unassigned P1 past its target, a live thread
// with an internal note, a paused Waiting, an overridden priority, a resolved
// ticket with its summary, and one on hold with a supplier. Timestamps are
// relative, so the demo never looks stale.
//
// The last two are ServiceTitan, and they are here for a specific reason: the
// ServiceTitan team works its own tickets in this tool (Grant, 2026-08-17), so
// filtering the queue to ServiceTitan has to show real work rather than an empty
// state. Without them that filter is blank on a fresh browser and the
// ServiceTitan walkthrough has nothing to walk.
function seed() {
  return {
    nextNumber: FIRST_TICKET_NUMBER + 8,
    rules: PRIORITY_RULES.map((r) => ({ ...r })),
    sla: SLA_POLICY.map((p) => ({ ...p })),
    tickets: [
      {
        number: 'TICK-501',
        requester: { name: 'Marcus Webb', email: 'mwebb@intermountainhs.com', department: 'Field', role: 'Technician', brand: 'Superior', isLeadership: false },
        categoryId: 'network',
        requestTypeId: 'network-wifi',
        formData: { location: 'Superior yard', devices: 'several_devices', frequency: 'constant', since: 'today', othersAffected: 'whole_office' },
        note: 'Laptop says connected but nothing loads. Two other techs having the same thing.',
        attachments: [],
        priority: 'P1',
        priorityRuleId: 'r-multiple',
        priorityRuleName: 'Site-wide outage',
        priorityOverride: null,
        status: 'new',
        waitingReason: null,
        assignee: null,
        raisedAt: ago(2.5 * HOUR),
        firstResponseAt: null,
        resolvedAt: null,
        closedAt: null,
        waitingSince: null,
        pausedMins: 0,
        similarCount: 6,
        messages: [],
        events: [
          { id: 'e1', at: ago(2.5 * HOUR), type: 'priority', text: 'Priority set to P1 by rule "Site-wide outage"' },
        ],
        summary: null,
      },
      {
        number: 'TICK-502',
        requester: { name: 'Marisol Vega', email: 'mvega@intermountainhs.com', department: 'Call Center', role: 'CSR', brand: 'Beehive Plumbing', isLeadership: false },
        categoryId: 'phones',
        requestTypeId: 'phone-dropping',
        formData: { what: 'call_drops_mid_conversation', frequency: 'every', since: 'today', othersAffected: 'my_team' },
        note: 'Started right after the update this morning. Three of us on the same shift.',
        attachments: [],
        priority: 'P2',
        priorityRuleId: 'r-team-wide',
        priorityRuleName: 'A whole team or office affected',
        priorityOverride: null,
        status: 'in_progress',
        waitingReason: null,
        assignee: 'jake',
        raisedAt: ago(3 * HOUR),
        firstResponseAt: ago(2.4 * HOUR),
        resolvedAt: null,
        closedAt: null,
        waitingSince: null,
        pausedMins: 0,
        similarCount: 2,
        messages: [
          { id: 'm1', author: 'Jake Dupaix', authorType: 'it', isInternal: false, body: 'Looking now — I can see three reports from the call center on the same queue. Is outbound working?', at: ago(2.4 * HOUR) },
          { id: 'm2', author: 'Marisol Vega', authorType: 'requester', isInternal: false, body: 'Outbound is fine. It is only calls coming in.', at: ago(2.1 * HOUR) },
          { id: 'm3', author: 'Jake Dupaix', authorType: 'it', isInternal: true, body: 'Zoom CC status page shows a routing incident in our region. Not our config. Holding off on changing the queue until they update.', at: ago(2 * HOUR) },
        ],
        events: [
          { id: 'e1', at: ago(3 * HOUR), type: 'priority', text: 'Priority set to P2 by rule "A whole team or office affected"' },
          { id: 'e2', at: ago(2.5 * HOUR), type: 'assign', text: 'Jake Dupaix assigned this to himself' },
        ],
        summary: null,
      },
      {
        number: 'TICK-503',
        requester: { name: 'Alicia Rhodes', email: 'arhodes@intermountainhs.com', department: 'Accounting', role: 'AP Specialist', brand: 'Intermountain Home Services', isLeadership: false },
        categoryId: 'security',
        requestTypeId: 'sec-phishing',
        formData: { action: 'i_clicked_a_link', frequency: 'once', since: 'today', othersAffected: 'dont_know' },
        note: 'Looked like a DocuSign from a vendor we actually use.',
        attachments: [],
        priority: 'P1',
        priorityRuleId: 'r-security',
        priorityRuleName: 'Security incident',
        priorityOverride: null,
        status: 'waiting',
        waitingReason: 'requester',
        assignee: 'colby',
        raisedAt: ago(5 * HOUR),
        firstResponseAt: ago(4.9 * HOUR),
        resolvedAt: null,
        closedAt: null,
        waitingSince: ago(3 * HOUR),
        pausedMins: 0,
        similarCount: 1,
        messages: [
          { id: 'm1', author: 'Colby Johnson', authorType: 'it', isInternal: false, body: 'Thanks for reporting it. I have reset your password and signed you out everywhere. Can you confirm you did not enter credentials on the page after clicking?', at: ago(4.9 * HOUR) },
          { id: 'm2', author: 'Colby Johnson', authorType: 'it', isInternal: true, body: 'Sign-in logs are clean — no foreign IPs. Holding as waiting-on-requester until she confirms, then close it out.', at: ago(3 * HOUR) },
        ],
        events: [
          { id: 'e1', at: ago(5 * HOUR), type: 'priority', text: 'Priority set to P1 by rule "Security incident"' },
          { id: 'e2', at: ago(4.95 * HOUR), type: 'assign', text: 'Assigned to Colby Johnson' },
          { id: 'e3', at: ago(3 * HOUR), type: 'status', text: 'Waiting on requester — clock paused' },
        ],
        summary: null,
      },
      {
        number: 'TICK-504',
        requester: { name: 'Ken Alsop', email: 'kalsop@intermountainhs.com', department: 'Operations', role: 'General Manager', brand: 'Comfort Solutions', isLeadership: true },
        categoryId: 'devices',
        requestTypeId: 'device-monitor',
        formData: { symptom: 'dock_not_connecting', frequency: 'few_daily', since: 'this_week', othersAffected: 'just_me' },
        note: '',
        attachments: [],
        priority: 'P3',
        priorityRuleId: 'r-leadership',
        priorityRuleName: 'Exec or GM blocked',
        priorityOverride: { from: 'P2', to: 'P3', reason: 'Second monitor only — he has a working screen. Not a P2.', by: 'Jake Dupaix', at: ago(20 * HOUR) },
        status: 'in_progress',
        waitingReason: null,
        assignee: 'jake',
        raisedAt: ago(26 * HOUR),
        firstResponseAt: ago(21 * HOUR),
        resolvedAt: null,
        closedAt: null,
        waitingSince: null,
        pausedMins: 0,
        similarCount: 1,
        messages: [
          { id: 'm1', author: 'Jake Dupaix', authorType: 'it', isInternal: false, body: 'I have a replacement dock on the shelf. Are you in the Sandy office tomorrow?', at: ago(21 * HOUR) },
        ],
        events: [
          { id: 'e1', at: ago(26 * HOUR), type: 'priority', text: 'Priority set to P2 by rule "Exec or GM blocked"' },
          { id: 'e2', at: ago(20 * HOUR), type: 'priority', text: 'Priority changed P2 → P3 by Jake Dupaix' },
        ],
        summary: null,
      },
      {
        number: 'TICK-505',
        requester: { name: 'Priya Raman', email: 'praman@intermountainhs.com', department: 'Install', role: 'Install Coordinator', brand: 'Superior', isLeadership: false },
        categoryId: 'accounts',
        requestTypeId: 'account-locked',
        formData: { system: 'servicetitan', tried: 'yes_it_failed', frequency: 'every', since: 'today', othersAffected: 'just_me' },
        note: '',
        attachments: [],
        priority: 'P2',
        priorityRuleId: 'r-lockout',
        priorityRuleName: 'Locked out of an account',
        priorityOverride: null,
        status: 'resolved',
        waitingReason: null,
        assignee: 'colby',
        raisedAt: ago(30 * HOUR),
        firstResponseAt: ago(29.5 * HOUR),
        resolvedAt: ago(28 * HOUR),
        closedAt: null,
        waitingSince: null,
        pausedMins: 0,
        similarCount: 1,
        messages: [
          { id: 'm1', author: 'Colby Johnson', authorType: 'it', isInternal: false, body: 'Reset and unlocked. Try again and let me know.', at: ago(29.5 * HOUR) },
          { id: 'm2', author: 'Priya Raman', authorType: 'requester', isInternal: false, body: "That's got it, thank you.", at: ago(28.2 * HOUR) },
        ],
        events: [
          { id: 'e1', at: ago(30 * HOUR), type: 'priority', text: 'Priority set to P2 by rule "Locked out of an account"' },
          { id: 'e2', at: ago(28 * HOUR), type: 'resolve', text: 'Resolved by Colby Johnson' },
        ],
        summary: {
          problem: 'Locked out of ServiceTitan after repeated failed sign-ins; self-service reset also failed.',
          cause: 'Account lockout threshold hit. The self-service reset failed because her recovery phone was a former number.',
          fix: 'Unlocked the account, reset the password, and updated the recovery number to her current mobile.',
          watchFor: 'Other long-tenured staff may still have old recovery numbers on file — worth a sweep.',
          at: ago(28 * HOUR),
          reviewed: false,
        },
      },
      {
        number: 'TICK-506',
        requester: { name: 'Travis Boone', email: 'tboone@intermountainhs.com', department: 'Field', role: 'Lead Technician', brand: 'Walker', isLeadership: false },
        categoryId: 'hardware',
        requestTypeId: 'hw-request',
        formData: { item: 'tablet', why: 'existing_one_is_broken', manager: 'Dave Kimball', loaner: 'yes_a_loaner_please', neededBy: 'this_week' },
        note: 'Screen cracked on a job. Still usable but I cannot see the bottom third.',
        attachments: [],
        priority: 'P4',
        priorityRuleId: 'r-hardware',
        priorityRuleName: 'New hardware request',
        priorityOverride: null,
        status: 'waiting',
        waitingReason: 'third_party',
        assignee: 'grant',
        raisedAt: ago(4 * 24 * HOUR),
        firstResponseAt: ago(3.8 * 24 * HOUR),
        resolvedAt: null,
        closedAt: null,
        waitingSince: ago(2 * 24 * HOUR),
        pausedMins: 0,
        similarCount: 1,
        messages: [
          { id: 'm1', author: 'Grant Myers', authorType: 'it', isInternal: false, body: 'Dave has approved it. Loaner is at the front desk — the replacement is ordered.', at: ago(3.8 * 24 * HOUR) },
          { id: 'm2', author: 'Grant Myers', authorType: 'it', isInternal: true, body: 'Supplier says 7–10 days. Do not close this until the real unit is in his hands.', at: ago(2 * 24 * HOUR) },
        ],
        events: [
          { id: 'e1', at: ago(4 * 24 * HOUR), type: 'priority', text: 'Priority set to P4 by rule "New hardware request"' },
          { id: 'e2', at: ago(2 * 24 * HOUR), type: 'status', text: 'On hold — supplier. Clock paused' },
        ],
        summary: null,
      },
      {
        // ServiceTitan, unassigned, so the ServiceTitan queue has something to
        // pick up. P3 comes from the pinned default rule — no rule matches a
        // routine ServiceTitan request, which is itself a question for Rhea.
        number: 'TICK-507',
        requester: { name: 'Dana Whitlock', email: 'dwhitlock@intermountainhs.com', department: 'Sales', role: 'Sales Manager', brand: 'Master Rooter', isLeadership: false },
        categoryId: 'servicetitan',
        requestTypeId: 'st-pricebook',
        kind: 'request',
        formData: { change: 'change_a_price', detail: 'Water heater flush — the 2026 rate', neededBy: 'this_week' },
        note: 'Techs are quoting the old price in the field. Sooner the better but nobody is stuck.',
        attachments: [],
        priority: 'P3',
        priorityRuleId: 'r-default',
        priorityRuleName: 'Default — nothing matched',
        priorityOverride: null,
        status: 'new',
        waitingReason: null,
        assignee: null,
        raisedAt: ago(6 * HOUR),
        firstResponseAt: null,
        resolvedAt: null,
        closedAt: null,
        waitingSince: null,
        pausedMins: 0,
        similarCount: 1,
        messages: [],
        events: [
          { id: 'e1', at: ago(6 * HOUR), type: 'priority', text: 'Priority set to P3 by rule "Default — nothing matched"' },
        ],
        summary: null,
      },
      {
        // ServiceTitan, in flight and paused on the requester, so the paused
        // clock can be shown on a ServiceTitan ticket rather than borrowed from
        // an IT one.
        number: 'TICK-508',
        requester: { name: 'Hector Salas', email: 'hsalas@intermountainhs.com', department: 'Operations', role: 'Service Manager', brand: 'Beehive Plumbing', isLeadership: false },
        categoryId: 'servicetitan',
        requestTypeId: 'st-licence',
        kind: 'request',
        formData: { who: 'Renae Tuttle', change: 'change_licence_type', reason: 'Moving from the call center onto dispatch this month.', neededBy: 'this_week' },
        note: '',
        attachments: [],
        priority: 'P3',
        priorityRuleId: 'r-default',
        priorityRuleName: 'Default — nothing matched',
        priorityOverride: null,
        status: 'waiting',
        waitingReason: 'requester',
        assignee: 'grant',
        raisedAt: ago(2 * 24 * HOUR),
        firstResponseAt: ago(1.9 * 24 * HOUR),
        resolvedAt: null,
        closedAt: null,
        waitingSince: ago(1.5 * 24 * HOUR),
        pausedMins: 0,
        similarCount: 1,
        messages: [
          { id: 'm1', author: 'Grant Myers', authorType: 'it', isInternal: false, body: 'Happy to switch her over. Which start date should I use, and does she keep the call center licence in the meantime?', at: ago(1.9 * 24 * HOUR) },
          { id: 'm2', author: 'Grant Myers', authorType: 'it', isInternal: true, body: 'Dispatch seats are at the limit — one has to come off before this goes on. Holding until he confirms the date.', at: ago(1.5 * 24 * HOUR) },
        ],
        events: [
          { id: 'e1', at: ago(2 * 24 * HOUR), type: 'priority', text: 'Priority set to P3 by rule "Default — nothing matched"' },
          { id: 'e2', at: ago(1.95 * 24 * HOUR), type: 'assign', text: 'Assigned to Grant Myers' },
          { id: 'e3', at: ago(1.5 * 24 * HOUR), type: 'status', text: 'Waiting on requester — clock paused' },
        ],
        summary: null,
      },
    ],
  };
}

// ------------------------------------------------------------------- store

let state = null;
const listeners = new Set();

function load() {
  if (state) return state;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // A shape change between demo builds must not strand someone on a broken
      // page with no obvious way out — fall through to a fresh seed instead.
      if (parsed && Array.isArray(parsed.tickets)) {
        state = parsed;
        return state;
      }
    }
  } catch {
    /* corrupt or unavailable storage → seed */
  }
  state = seed();
  save();
  return state;
}

function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing, or the image attachments blew the quota. The demo keeps
    // working in memory for this tab; only persistence is lost.
  }
  listeners.forEach((fn) => fn());
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function resetDemo() {
  state = seed();
  save();
}

// Every read and write goes through this so the prototype has the same latency
// shape as the real thing — enough that a missing loading state is visible.
const settle = (value) => new Promise((resolve) => setTimeout(() => resolve(value), 120));

const clone = (v) => JSON.parse(JSON.stringify(v));

// ------------------------------------------------------- reads (→ GET /api)

/** → GET /api/reference */
export async function getReference() {
  const s = load();
  return settle({ rules: clone(s.rules), sla: clone(s.sla), calendar: WORK_CALENDAR, staff: IT_STAFF });
}

/* ------------------------------------------------ WHICH FORMS FILE FOR REAL
 *
 * Every form, in the normal build (S08 part A, Grant 2026-10-28). The
 * walkthrough build files every form to this localStorage prototype instead,
 * because Grant's walkthrough scripts are written against it (2026-08-25) and a
 * demo session must never send anything. One helper decides, for the store and
 * the form alike: lib/filesForReal.js.
 *
 * `walkthrough` is passed only by tests; the build decides it otherwise.
 */
const isServerType = (requestTypeId, walkthrough) =>
  filesForReal(typeById(requestTypeId), walkthrough === undefined ? undefined : { walkthrough });

/* Numbers the API has handed us. A ticket in here is authoritative on the
 * SERVER: replies go to the message API, and the lifecycle actions the server
 * cannot do yet are refused out loud instead of editing a browser copy. */
const serverNumbers = new Set();
export const isServerTicket = (number) => serverNumbers.has(number);

/* WHICH STORE AN ACTION TOUCHES. The screen says what it is showing - 'real' or
 * 'demo' - and the action follows THAT. It must not guess from the number: a
 * demo ticket and a real ticket can share one, and a demo ticket on screen must
 * never write to the server (review of #72, finding 1). The number registry is
 * only the fallback for a caller that does not say. */
const routesToServer = (number, source) => (source ? source === 'real' : serverNumbers.has(number));

/** Tell every screen "something changed, read it again" - the same signal save() sends. */
function emit() {
  listeners.forEach((fn) => fn());
}

/* Why a reply to a filed ticket did not come back as saved.
 *   outcome 'rejected' - the server answered with a definite refusal; nothing was stored.
 *   outcome 'unknown'  - no usable answer; it MAY be stored. Retry with the SAME messageId
 *                        so the server recognises it, instead of guessing "not saved". */
export class SaveOutcome extends Error {
  /* `code` is the server's name for a refusal. CLOSED (409) means the ticket is closed or past its
   * reopen window: `followUpOf` is then the number a new ticket follows up. */
  constructor(outcome, status, { code = null, followUpOf = null } = {}) {
    super(outcome === 'rejected' ? `the server refused the message (${status})` : 'could not confirm whether the message saved');
    this.name = 'SaveOutcome';
    this.outcome = outcome;
    this.status = status;
    this.code = code;
    this.followUpOf = followUpOf;
  }
}
const DEFINITE_REJECTIONS = new Set([400, 401, 403, 404, 409, 413, 422]);

/** Ask every screen to re-read the store (used to let the user check the thread). */
export function refresh() {
  emit();
}

export class UnsupportedForServerTicket extends Error {
  constructor(action) {
    super(`${action} is not available on a filed ticket yet - the server has no command for it`);
    this.name = 'UnsupportedForServerTicket';
  }
}

async function api(path, init) {
  // A write carries the CSRF header; the server refuses one without it.
  const write = init?.method && init.method.toUpperCase() !== 'GET';
  const res = await guardedFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(write ? { 'x-requested-by': 'ihs-it-help' } : {}), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = new Error(`${init?.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status; // callers tell "not there" from "not allowed" from "down"
    // The server's own code and sentence, when it sent one. Only a caller that knows the
    // code may show the sentence (lib/onBehalf.js); everything else still reads .status.
    let body = null;
    try { body = await res.json(); } catch { body = null; }
    const e = body && typeof body === 'object' ? body.error : null;
    if (e && typeof e.code === 'string') err.code = e.code;
    if (e && typeof e.message === 'string') err.serverMessage = e.message;
    throw err;
  }
  return res.json();
}

/* Why a read of the real tickets did not give an answer. Three different
 * things the person must not be shown as one (HD-02, #53):
 *   notfound    - the server says there is no such ticket (404)
 *   forbidden   - there is one and it is not yours (403)
 *   unavailable - no usable answer at all: offline, 5xx, a page where JSON
 *                 should be. NOT the same as "nothing there". */
/* An answer that is null, missing a field, or the wrong shape is NOT "empty" and
 * NOT "not found" - it is an answer we cannot use, so it counts as unavailable
 * (no .status on the error). Review of #72, finding 2. */
const malformed = (what) => new Error(`the server sent ${what} we cannot read`);

function parseApiTicket(raw) {
  const ok = raw && typeof raw === 'object' && !Array.isArray(raw)
    && ['ticketNo', 'typeKey', 'status', 'raisedAt'].every((k) => typeof raw[k] === 'string' && raw[k].trim());
  if (!ok) throw malformed('a ticket');
  return fromApi(raw);
}

const problemOf = (err) => (err?.status === 404 ? 'notfound' : err?.status === 403 ? 'forbidden' : 'unavailable');

/* The API's ticket shape is NOT this file's shape - `ticketNo` vs `number`,
 * `typeKey` vs `requestTypeId`, a requester NAME where the prototype carries an
 * object. (A BACKLOG note calls the two byte-identical; it is wrong - read
 * api/src/shared/ticketRead.js.) Every field TicketDetail touches gets a
 * defined value here, because a missing array crashes a render and a missing
 * string only leaves a blank.
 *
 * A REQUESTER gets a deliberately thin projection from the server - no
 * priority, no assignee, no assessment. Those arrive undefined and the page
 * simply shows less. That is the privacy boundary working, not a gap to fill.
 * On the single-ticket read they DO get their own formData and note back
 * (AR-02 readback); the list of tickets never carries them.
 */
/* The database allows a ticket with no requester name, and the API passes it on as
 * null (or, if something upstream is wrong, a number or an object). The screens
 * call string methods on this name, so a bad one crashed the whole ticket page -
 * and with it the open composer and its draft (review of #72). Anything that is
 * not a real, non-blank string becomes a plain word. */
const FALLBACK_REQUESTER = 'Requester';
const requesterNameOf = (v) => (typeof v === 'string' && v.trim() ? v : FALLBACK_REQUESTER);

// A time the server sent, or null. Anything that is not a readable time is null, never "1970".
const timeOf = (v) => (typeof v === 'string' && v.trim() && !Number.isNaN(new Date(v).getTime()) ? v : null);
const minsOf = (v) => (typeof v === 'number' && v > 0 ? v : 0);
// A ticket number the server sent, or null. It goes into links, so nothing else gets through.
export const ticketNoOf = (v) => (typeof v === 'string' && /^[A-Za-z]{1,10}-\d{1,12}$/.test(v.trim()) ? v.trim().toUpperCase() : null);

/* The resolution summary (staff only - the server reads it only for the desk). Each part is text or
 * empty; anything else is not a summary and is dropped. */
function summaryOf(s) {
  if (!s || typeof s !== 'object' || typeof s.fix !== 'string') return null;
  const text = (v) => (typeof v === 'string' ? v : '');
  return {
    problem: text(s.problem), cause: text(s.cause), fix: s.fix, watchFor: text(s.watchFor),
    by: text(s.resolvedBy), at: timeOf(s.resolvedAt), reviewed: s.reviewed === true,
  };
}

function fromApi(t) {
  if (!t) return null;
  serverNumbers.add(t.ticketNo);
  return {
    number: t.ticketNo,
    requester: { name: requesterNameOf(t.requesterName), email: null, role: '', brand: '' },
    // Who typed it, when that was someone else (a manager raising it for one of their people).
    // Null for a ticket the person raised themselves.
    raisedByName: typeof t.raisedByName === 'string' && t.raisedByName.trim() ? t.raisedByName.trim() : null,
    categoryId: t.categoryKey,
    requestTypeId: t.typeKey,
    kind: kindOf(t.typeKey),
    formData: t.formData || {},
    note: t.note || '',
    attachments: [],
    priority: t.priority || null,
    priorityRuleName: t.priorityRuleName || '',
    priorityOverride: null,
    status: t.status,
    // Both views (S07b part 3): when it was resolved, and what a hold waits on.
    waitingReason: t.waitingReason === 'requester' || t.waitingReason === 'third_party' ? t.waitingReason : null,
    assignee: t.assignee || null,
    // Staff only. The version a command must quote, and whether the server will take commands.
    version: Number.isInteger(t.version) ? t.version : null,
    canCommand: t.canCommand === true,
    // Staff only: "I have this" on a P1 (S10 part C). The server sends the address.
    acknowledgedAt: timeOf(t.acknowledgedAt),
    acknowledgedBy: typeof t.acknowledgedBy === 'string' && t.acknowledgedBy ? t.acknowledgedBy : null,
    lifecycleReady: t.lifecycleReady === true,
    raisedAt: t.raisedAt,
    resolvedAt: timeOf(t.resolvedAt),
    // Until when a resolved ticket can be reopened (the server counts it). Null: not resolved, or
    // the server could not say - then the page offers neither "Still not fixed" nor "Start a new one".
    reopenUntil: timeOf(t.reopenUntil),
    // The follow-up links (both views; the server lists only tickets this reader may open).
    followUpOf: ticketNoOf(t.followUpOf),
    followUps: Array.isArray(t.followUps) ? t.followUps.map(ticketNoOf).filter(Boolean) : [],
    // Only for someone who raised this ticket FOR another person: who a follow-up is for.
    raisedForUpn: typeof t.raisedForUpn === 'string' && t.raisedForUpn.includes('@') ? t.raisedForUpn : null,
    // Staff only: the clocks. The server keeps the due times and moves them when a hold ends or the
    // priority changes, so the meters count from them (lib/ticketView.js slaFor).
    firstResponseAt: timeOf(t.firstResponseAt),
    waitingSince: timeOf(t.waitingSince),
    responseDueAt: timeOf(t.responseDueAt),
    resolveDueAt: timeOf(t.resolveDueAt),
    pausedMins: minsOf(t.pausedMins),
    pausedWallMins: minsOf(t.pausedWallMins),
    similarCount: 1,
    messages: [],
    events: [],
    summary: summaryOf(t.summary),
    // IT-only, attached by the server. Undefined for a requester, which is what
    // makes SeatAssessmentPanel disappear without any role check in the browser.
    assessment: t.assessment,
    intakeTurns: t.intakeTurns || [],
    fromServer: true,
    isDemo: false,
  };
}

/** → GET /api/tickets?mine=1 | GET /api/tickets (IT queue)
 *
 * Returns { rows, serverState, hiddenDemo }.
 *   rows        real tickets AND demo tickets, each marked: a real one has
 *               fromServer true, a demo one isDemo true.
 *   serverState 'ok' | 'forbidden' | 'unavailable'. When it is not 'ok' the real
 *               list could not be read and the screen MUST say so - rows then
 *               holds only demo tickets, and showing them as a normal queue is
 *               the failure this was written to stop (HD-02, #53).
 *   hiddenDemo  demo ticket numbers dropped because a REAL ticket has the same
 *               number. The real one owns the number, always.
 *
 * Demo tickets stay in the walkthrough build only: Grant's walkthrough scripts are
 * written against them, and there they are labelled, never mixed in unmarked. The
 * live app never reads them (S08 part B), so rows holds real tickets only and
 * hiddenDemo is empty. */
export async function loadTickets({ mine, email, walkthrough = isWalkthroughBuild() } = {}) {
  const s = load();
  const demoRows = !walkthrough ? [] : mine ? s.tickets.filter((t) => t.requester.email === email) : s.tickets;

  let server = [];
  let serverState = 'ok';
  try {
    const body = await api(`/api/tickets${mine ? '?mine=1' : ''}`);
    if (!body || !Array.isArray(body.tickets)) throw malformed('a ticket list');
    server = body.tickets.map(parseApiTicket);
  } catch (err) {
    server = [];
    serverState = problemOf(err) === 'forbidden' ? 'forbidden' : 'unavailable';
  }

  const realNumbers = new Set(server.map((t) => t.number));
  const demo = clone(demoRows)
    .filter((t) => !realNumbers.has(t.number))
    .map((t) => ({ ...t, isDemo: true }));
  const hiddenDemo = demoRows.filter((t) => realNumbers.has(t.number)).map((t) => t.number);

  return settle({
    rows: [...demo, ...server].sort((a, b) => new Date(b.raisedAt) - new Date(a.raisedAt)),
    serverState,
    hiddenDemo,
  });
}

/** The rows alone, for callers that do not need the failure signal. */
export async function listTickets(opts) {
  return (await loadTickets(opts)).rows;
}

/** → GET /api/tickets/{number}
 *
 * Returns { status, ticket }. status is 'ok', 'notfound', 'forbidden' or
 * 'unavailable' (see problemOf). The SERVER is asked first, for every number,
 * so a real ticket can never be shadowed by a demo ticket with the same number.
 * A demo ticket is served only when the server says there is no such ticket, or
 * cannot be reached - and then it is marked isDemo (and serverUnavailable when
 * the server could not be asked) so the page can say what it is showing. The live
 * app never falls back to a demo ticket: it shows what the server said. */
export async function getTicketResult(number, { walkthrough = isWalkthroughBuild() } = {}) {
  const s = load();
  const local = walkthrough ? s.tickets.find((t) => t.number === number) : undefined;
  const asDemo = (extra) => settle({ status: 'ok', ticket: { ...clone(local), isDemo: true, reopenUntil: demoReopenUntil(local), ...extra } });

  let t;
  try {
    const data = await api(`/api/tickets/${encodeURIComponent(number)}`);
    t = parseApiTicket(data?.ticket);
    // An answer about a DIFFERENT ticket is not an answer about this one.
    if (t.number !== number) throw malformed('another ticket');
  } catch (err) {
    const problem = problemOf(err);
    if (local && problem === 'notfound') return asDemo({});
    if (local && problem === 'unavailable') return asDemo({ serverUnavailable: true });
    return settle({ status: problem, ticket: null });
  }

  /* The thread is read from the server every time - never initialised empty.
   * If THAT read fails the ticket still opens, but the page is told the
   * conversation is unknown (threadError) so it cannot show "nothing said
   * yet" over a thread that may be full. The API filters internal notes for
   * a requester; this does not re-filter and does not need to. */
  try {
    const { messages, canWriteInternal } = await api(`/api/tickets/${encodeURIComponent(number)}/messages`);
    if (!Array.isArray(messages)) throw new Error('no messages array');
    // The server says whether this caller may write internal notes. Absent
    // or anything but true means no: the screen must not offer one.
    t.canNote = canWriteInternal === true;
    t.messages = messages.map((m) => ({
      id: m.id,
      author: m.authorName,
      authorType: m.authorType,
      isInternal: Boolean(m.isInternal),
      body: m.body,
      at: m.createdAt,
    }));
  } catch {
    t.threadError = true;
  }
  return { status: 'ok', ticket: t };
}

/** The ticket alone, or null. Kept for callers that do not need the reason. */
export async function getTicket(number, opts) {
  return (await getTicketResult(number, opts)).ticket;
}

// ---------------------------------------------------- the priority preview

/** How many of the same request type landed in the last hour, plus this one. */
function similarCountFor(requestTypeId, now) {
  const s = load();
  return (
    s.tickets.filter(
      (t) =>
        t.requestTypeId === requestTypeId &&
        now - new Date(t.raisedAt) < HOUR &&
        !['resolved', 'closed'].includes(t.status)
    ).length + 1
  );
}

/**
 * What priority this draft WOULD get, evaluated live as the form is filled in.
 *
 * Synchronous and side-effect free on purpose: it runs on every keystroke, and
 * it is the whole argument for rules over a model made visible — the requester
 * sees the priority and the rule that produced it before they submit, and IT
 * can point at the same rule afterwards. In the real build this is the same
 * pure `evaluate()` running server-side; the client gets it back with the
 * reference data rather than re-implementing it.
 */
export function previewPriority({ categoryId, requestTypeId, formData, requester }) {
  const s = load();
  const now = new Date();
  const draft = { categoryId, requestTypeId, formData, brand: requester.brand, kind: kindOf(requestTypeId) };
  const similarCount = similarCountFor(requestTypeId, now);
  const facts = factsFor({ ticket: draft, requester, similarCount, now });
  return { ...evaluate(s.rules, facts), similarCount };
}

// ------------------------------------------------ writes (→ POST/PATCH /api)

/** → POST /api/tickets. Returns the created ticket, priority already resolved.
 * `followUpOf` (a closed ticket's number) files it as a follow-up of that ticket; only a form that
 * files for real can carry it. When the server filed it WITHOUT the link, the ticket comes back with
 * `followUpLinked: false` so the receipt can say so. */
export async function createTicket({ categoryId, requestTypeId, formData, note, attachments, requester, forUpn, operationKey, followUpOf }, { walkthrough } = {}) {
  /* A real form files for real: a server row and its number. Only the Claude seat form
   * also emails IT on create (the seat recommendation); no other form notifies anyone
   * yet - see BACKLOG. Deliberately NOT wrapped in a try/catch - if the request did not file, the
   * person must be told, not shown a receipt for a ticket nobody has. NewTicket
   * surfaces the throw.
   *
   * `attachments` (the demo picture box) are not sent. A real form shows the file
   * picker instead, and NewTicket sends those files after the ticket is filed (S09). */
  if (isServerType(requestTypeId, walkthrough)) {
    assertLiveWritesAllowed();
    const followUp = ticketNoOf(followUpOf);
    const { ticketNo, followUpLinked } = await api('/api/tickets', {
      method: 'POST',
      body: JSON.stringify({
        typeKey: requestTypeId,
        formData,
        note: note || '',
        raisedByName: requester?.name,
        // A manager raising it for someone below them. The server checks the reporting line
        // again and refuses anyone else; absent = for yourself.
        ...(forUpn ? { requester: { upn: forUpn } } : {}),
        // Sent only for a follow-up: an ordinary ticket carries no followUpOf at all.
        ...(followUp ? { followUpOf: followUp } : {}),
        // The retry key for this draft. The server ignores it unless atomic save is on.
        operationKey,
      }),
    });
    // Read back rather than guess. The SERVER set the number, the priority and
    // the SLA clock, and this is the only way the receipt shows what was really
    // written instead of a second, client-side opinion of it.
    // The ticket IS filed by here. If the read-back fails or is unreadable, still hand
    // back the receipt: an error now would tell the person it was NOT filed.
    let ticket = null;
    try {
      ({ ticket } = await api(`/api/tickets/${encodeURIComponent(ticketNo)}`));
    } catch (err) {
      ticket = null;
    }
    // A read-back about a DIFFERENT ticket is not this receipt: fall back to the number it filed.
    const filed = (ticket && ticket.ticketNo === ticketNo ? fromApi(ticket) : null) || { number: ticketNo, formData, note: note || '', attachments: [] };
    return followUp ? { ...filed, followUpOf: followUp, followUpLinked: followUpLinked !== false } : filed;
  }

  const s = load();
  const now = new Date();

  // Drawn BEFORE the insert, atomically, exactly as NEXTVAL will be. Snowflake
  // has no RETURNING clause, and reading back "the newest row for this user"
  // returns the wrong ticket the moment two are raised in the same second.
  // Never take a number the server has already handed out (HD-02, #53).
  while (serverNumbers.has(`TICK-${s.nextNumber}`)) s.nextNumber += 1;
  const number = `TICK-${s.nextNumber}`;
  s.nextNumber += 1;

  const similarCount = similarCountFor(requestTypeId, now);
  const draft = { categoryId, requestTypeId, formData, brand: requester.brand, kind: kindOf(requestTypeId) };
  const facts = factsFor({ ticket: draft, requester, similarCount, now });
  const verdict = evaluate(s.rules, facts);

  const ticket = {
    number,
    requester: { ...requester },
    categoryId,
    requestTypeId,
    kind: kindOf(requestTypeId),
    formData: { ...formData },
    note: note || '',
    attachments: attachments || [],
    priority: verdict.priority,
    priorityRuleId: verdict.ruleId,
    priorityRuleName: verdict.ruleName,
    priorityOverride: null,
    status: 'new',
    waitingReason: null,
    assignee: null,
    raisedAt: now.toISOString(),
    firstResponseAt: null,
    resolvedAt: null,
    closedAt: null,
    waitingSince: null,
    pausedMins: 0,
    similarCount,
    messages: [],
    events: [
      {
        id: `e-${Date.now()}`,
        at: now.toISOString(),
        type: 'priority',
        text: `Priority set to ${verdict.priority} by rule "${verdict.ruleName || 'default'}"`,
      },
    ],
    summary: null,
  };

  s.tickets.unshift(ticket);
  save();
  return settle(clone(ticket));
}

/** → POST /api/tickets/{number}/messages */
export async function postMessage(number, { author, authorType, isInternal, body, messageId, source, stillNotFixed = false }) {
  if (routesToServer(number, source)) {
    /* A filed ticket: persist FIRST, report after. Anything but a 2xx throws,
     * and the composer keeps the draft. `author`/`authorType` are ignored on
     * purpose - the server derives both from the signed-in user, and the demo
     * persona must not be able to claim to be IT. `messageId` is the retry key:
     * the same id re-sent after a timeout lands on the saved row.
     *
     * Resolves to the server's receipt: { messageSaved, isInternal, emailStatus }.
     * emailStatus 'failed' is STILL a saved message - do not offer a re-send.
     * `stillNotFixed` (the requester's button on a resolved ticket) asks the server to reopen it
     * with the reply; it is sent only when true, so a plain reply is exactly what it always was. */
    // Before the try: a walkthrough refusal is "nothing was sent", never the
    // "it may have been saved" of an unknown outcome.
    assertLiveWritesAllowed();
    let res;
    try {
      res = await guardedFetch(`/api/tickets/${encodeURIComponent(number)}/messages`, {
        method: 'POST',
        // The server refuses a write without this header (CSRF guard).
        headers: { 'Content-Type': 'application/json', 'x-requested-by': 'ihs-it-help' },
        body: JSON.stringify({ body, isInternal: Boolean(isInternal), messageId, ...(stillNotFixed === true ? { stillNotFixed: true } : {}) }),
      });
    } catch {
      // No response at all: the server may or may not have stored it.
      throw new SaveOutcome('unknown', 0);
    }
    if (!res.ok) {
      let failed = null;
      try {
        failed = (await res.json())?.error || null;
      } catch {
        /* no readable body: the status alone decides */
      }
      const code = typeof failed?.code === 'string' ? failed.code : null;
      // Only a 4xx is a definite "not stored". A 5xx / gateway error cannot tell us whether the
      // INSERT ran before the failure - except a 503 NOT_READY, which ticketMessages.js answers
      // before anything is written.
      const rejected = DEFINITE_REJECTIONS.has(res.status) || (res.status === 503 && code === 'NOT_READY');
      throw new SaveOutcome(rejected ? 'rejected' : 'unknown', res.status, {
        code: rejected ? code : null,
        followUpOf: rejected && code === 'CLOSED' ? ticketNoOf(failed?.followUpOf) : null,
      });
    }
    let receipt = null;
    try {
      receipt = await res.json();
    } catch {
      /* a body we cannot read does not tell us the save failed */
    }
    if (!receipt?.messageSaved) throw new SaveOutcome('unknown', res.status);
    // Refresh: this ticket's thread and every list/detail reading the store.
    emit();
    // Unread: the writer has now seen this thread. Best effort - the message is
    // already saved and this must never undo or fail that.
    markSeen(number).catch(() => {});
    return receipt;
  }

  const s = load();
  const t = s.tickets.find((x) => x.number === number);
  if (!t) return settle(null);

  const now = new Date().toISOString();
  /* Closed, or resolved and past the window: a reply is refused, as on the server - the requester's,
   * and IT's too unless it is an internal note (planReply in api/src/shared/ticketCommand.js). */
  const until = demoReopenUntil(t);
  const pastWindow = t.status === 'closed' || Boolean(until && new Date(now) >= new Date(until));
  if (pastWindow && (authorType === 'requester' || !isInternal)) {
    throw new SaveOutcome('rejected', 409, { code: 'CLOSED', followUpOf: t.number });
  }
  t.messages.push({ id: `m-${Date.now()}`, author, authorType, isInternal: Boolean(isInternal), body, at: now });

  // First IT reply stops the response clock. An internal note is not a reply —
  // the requester has still heard nothing.
  if (authorType === 'it' && !isInternal && !t.firstResponseAt) {
    t.firstResponseAt = now;
    t.events.push({ id: `e-${Date.now()}`, at: now, type: 'reply', text: 'First response sent' });
  }

  // A requester reply un-blocks the ticket — but ONLY if the requester was what
  // it was blocked on.
  //
  // The two waiting reasons are not the same kind of wait. "Waiting on
  // requester" is IT's clock parked because IT asked a question, so the answer
  // arriving is exactly what restarts it. "On hold — supplier" is parked on
  // someone who is not in this conversation at all, and no amount of talking to
  // the requester makes the part arrive sooner. Restarting the clock there
  // would count a supplier's lead time against IT's SLA, which is the one thing
  // the pause exists to prevent.
  //
  // Either way the message lands in the thread and IT can see it. The only
  // question here is whose clock is running.
  if (authorType === 'requester' && t.status === 'waiting') {
    if (t.waitingReason === 'third_party') {
      // Recorded, not silent: IT must be able to see that the requester spoke
      // while the ticket sat on hold, without the status moving under them.
      t.events.push({
        id: `e-${Date.now()}-s`,
        at: now,
        type: 'status',
        text: 'Requester replied — still on hold for the supplier. Clock stays paused',
      });
    } else {
      applyPause(t, now);
      t.status = 'in_progress';
      t.waitingReason = null;
      t.events.push({ id: `e-${Date.now()}-s`, at: now, type: 'status', text: 'Requester replied — back to In progress. Clock resumed' });
    }
  }
  /* A resolved ticket reopens ONLY when the requester presses "Still not fixed", inside the reopen
   * window - as on the server (Grant, 2026-10-22: a "thanks!" must not reopen it). A plain reply is
   * just a message IT can read. Past the window the demo, like the server, keeps the words out. */
  if (authorType === 'requester' && t.status === 'resolved' && stillNotFixed === true) {
    t.status = t.assignee ? 'in_progress' : 'new';
    t.resolvedAt = null;
    t.events.push({ id: `e-${Date.now()}-r`, at: now, type: 'status', text: 'Reopened: the requester says it is still not fixed' });
  }

  save();
  return settle(clone(t));
}

/** Bank the working minutes a ticket spent in Waiting, then clear the marker. */
function applyPause(t, nowIso) {
  if (!t.waitingSince) return;
  const mins = (new Date(nowIso) - new Date(t.waitingSince)) / MIN;
  t.pausedMins = (t.pausedMins || 0) + Math.max(0, mins);
  t.waitingSince = null;
}

/** → PATCH /api/tickets/{number} — also the target of the queue's bulk actions. */
/* Why a staff command on a filed ticket did not come back as saved.
 *   outcome 'rejected' - the server refused for a named reason; nothing changed.
 *                        `code` says why: STALE (someone else changed it first),
 *                        NOT_ELIGIBLE, INVALID_TRANSITION, BAD_REQUEST, FORBIDDEN,
 *                        NOT_READY (503: the database is not ready for it), ...
 *   outcome 'unknown'  - no usable answer; it MAY have been saved. Send the SAME
 *                        operationKey again and the server finds the first save.
 * `message` is the server's own plain sentence, safe to show. */
export class CommandError extends Error {
  constructor(outcome, status, code, message) {
    super(message || (outcome === 'rejected' ? 'the server refused the change' : 'could not confirm whether the change saved'));
    this.name = 'CommandError';
    this.outcome = outcome;
    this.status = status;
    this.code = code || null;
  }
}

/** -> POST /api/tickets/{n}/commands. command: 'assign' | 'start' | 'priority' | 'hold' | 'resume' |
 *  'resolve' | 'reopen'.
 *  `expectedVersion` is the version the screen read; the server refuses (STALE) when the ticket
 *  has moved on. Resolves to the server's answer { ok, version, changed?, duplicate? }. */
export async function runCommand(number, command, args, { expectedVersion, operationKey }) {
  assertLiveWritesAllowed();
  let res;
  try {
    res = await guardedFetch(`/api/tickets/${encodeURIComponent(number)}/commands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-requested-by': 'ihs-it-help' },
      body: JSON.stringify({ command, args, expectedVersion, operationKey }),
    });
  } catch {
    throw new CommandError('unknown', 0);
  }
  let body = null;
  try {
    body = await res.json();
  } catch {
    /* a body we cannot read does not tell us the save failed */
  }
  if (!res.ok) {
    // A 503 the server NAMES is answered before anything is written (ticketCommands.js: NOT_READY,
    // LOOKUP_FAILED), so it is a refusal, not "it may have saved".
    const named503 = res.status === 503 && ['NOT_READY', 'LOOKUP_FAILED'].includes(body?.error?.code);
    const rejected = (DEFINITE_REJECTIONS.has(res.status) && res.status !== 500) || named503;
    throw new CommandError(rejected ? 'rejected' : 'unknown', res.status, body?.error?.code, rejected ? body?.error?.message : undefined);
  }
  if (!body || body.ok !== true) throw new CommandError('unknown', res.status);
  emit();
  return body;
}

/** -> POST /api/tickets/{number}/acknowledge. "I have this" on a REAL P1 (S10 part C). First tap
 * wins: the answer says who has it, and `alreadyAcknowledged` when it was someone else first. Throws
 * the api() error (.status, .code) when refused. There is no walkthrough version: the walkthrough
 * keeps its screens as they were. */
export async function acknowledgeTicket(number) {
  assertLiveWritesAllowed();
  const body = await api(`/api/tickets/${encodeURIComponent(number)}/acknowledge`, { method: 'POST', body: '{}' });
  emit(); // the page reads the ticket again and shows who has it
  return body;
}

/* The Admin screen's real settings (work hours, reopen window): GET/PUT /api/ticket-settings.
 * Only an IT Admin gets an answer; everyone else gets 403 (readAdminSettings throws with .status). */
export class AdminSettingsError extends Error {
  constructor(outcome, status, code, message) {
    super(message || (outcome === 'rejected' ? 'the server refused the change' : 'could not confirm whether the change saved'));
    this.name = 'AdminSettingsError';
    this.outcome = outcome; // 'rejected' (named reason, nothing changed) | 'unknown' (it MAY have saved)
    this.status = status;
    this.code = code || null; // STALE, BAD_REQUEST, NOT_READY, FORBIDDEN ...
  }
}

const shapeOk = (s) => s && typeof s === 'object'
  && s.calendar && Array.isArray(s.calendar.workDays) && Array.isArray(s.calendar.holidays) && Number.isInteger(s.calendar.version)
  && s.reopenWindow && Number.isInteger(s.reopenWindow.days) && Number.isInteger(s.reopenWindow.version);

/** -> GET /api/ticket-settings. Throws an error carrying .status (403 not an admin, 503 not turned on). */
export async function readAdminSettings() {
  const data = await api('/api/ticket-settings');
  if (!shapeOk(data)) throw malformed('settings');
  return data;
}

/** -> PUT /api/ticket-settings. section: 'calendar' | 'reopen'. Resolves to { changed, settings }. */
export async function saveAdminSettings(section, expectedVersion, values) {
  assertLiveWritesAllowed();
  let res;
  try {
    res = await guardedFetch('/api/ticket-settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-requested-by': 'ihs-it-help' },
      body: JSON.stringify({ section, expectedVersion, values }),
    });
  } catch {
    throw new AdminSettingsError('unknown', 0);
  }
  let body = null;
  try {
    body = await res.json();
  } catch {
    /* a body we cannot read does not tell us the save failed */
  }
  if (!res.ok) {
    const rejected = DEFINITE_REJECTIONS.has(res.status) || res.status === 503;
    throw new AdminSettingsError(rejected ? 'rejected' : 'unknown', res.status, body?.error?.code, rejected ? body?.error?.message : undefined);
  }
  // `settings: null` means the save landed but the server could not read it back: the screen reloads.
  if (!body || body.ok !== true || (body.settings !== null && !shapeOk(body.settings))) throw new AdminSettingsError('unknown', res.status);
  return { changed: body.changed === true, settings: body.settings };
}

/** -> GET /api/tickets/{n}/assignees. Who this ticket may be given to: [{ upn }]. */
export async function listAssignees(number) {
  const data = await api(`/api/tickets/${encodeURIComponent(number)}/assignees`);
  if (!Array.isArray(data?.assignees)) throw malformed('an assignee list');
  return data.assignees.filter((a) => a && typeof a.upn === 'string').map((a) => ({ upn: a.upn }));
}

export async function updateTicket(number, patch, { by, source }) {
  if (routesToServer(number, source)) throw new UnsupportedForServerTicket('Changing a ticket');
  const s = load();
  const t = s.tickets.find((x) => x.number === number);
  if (!t) return settle(null);
  const now = new Date().toISOString();
  const log = (type, text) =>
    t.events.push({ id: `e-${Date.now()}-${Math.round(Math.random() * 1e6)}`, at: now, type, text });

  if ('assignee' in patch && patch.assignee !== t.assignee) {
    const who = IT_STAFF.find((p) => p.id === patch.assignee);
    const self = who && by && who.name === by;
    t.assignee = patch.assignee;
    log('assign', who ? (self ? `${who.name} assigned this to themselves` : `Assigned to ${who.name}`) : 'Unassigned');
    if (t.status === 'new' && patch.assignee) {
      t.status = 'in_progress';
      log('status', 'Status → In progress');
    }
  }

  if ('status' in patch && patch.status !== t.status) {
    if (t.status === 'waiting') applyPause(t, now);
    if (patch.status === 'waiting') t.waitingSince = now;

    const from = t.status;
    t.status = patch.status;
    t.waitingReason = patch.status === 'waiting' ? patch.waitingReason || 'requester' : null;

    if (patch.status === 'waiting') {
      const label = t.waitingReason === 'requester' ? 'Waiting on requester' : 'On hold — supplier';
      log('status', `${label}. Clock paused`);
    } else {
      log('status', `Status → ${statusLabel(patch.status)}${from === 'waiting' ? '. Clock resumed' : ''}`);
    }
  }

  if ('priority' in patch && patch.priority !== t.priority) {
    t.priorityOverride = { from: t.priority, to: patch.priority, reason: patch.overrideReason || '', by, at: now };
    log('priority', `Priority changed ${t.priority} → ${patch.priority} by ${by}`);
    t.priority = patch.priority;
  }

  save();
  return settle(clone(t));
}

/** → POST /api/tickets/{number}/resolve — writes the summary with it. */
export async function resolveTicket(number, { by, summary, source }) {
  if (routesToServer(number, source)) throw new UnsupportedForServerTicket('Resolving a ticket');
  const s = load();
  const t = s.tickets.find((x) => x.number === number);
  if (!t) return settle(null);
  const now = new Date().toISOString();

  if (t.status === 'waiting') applyPause(t, now);
  t.status = 'resolved';
  t.waitingReason = null;
  t.resolvedAt = now;
  // Resolved before IT ever replied: the resolve is the first reply, as on the server (Grant, 2026-10-23).
  if (!t.firstResponseAt) t.firstResponseAt = now;
  // The summary saves ITSELF. Review is after the fact — a manual write-up step
  // is demonstrably the thing that gets skipped when the queue is busy (the
  // Asana board's `Resolution Made` field is empty on all 412 finished tickets).
  t.summary = { ...summary, at: now, reviewed: false };
  t.events.push({ id: `e-${Date.now()}`, at: now, type: 'resolve', text: `Resolved by ${by}` });

  save();
  return settle(clone(t));
}

// ------------------------------------------------ admin writes (→ /api/admin)

/** The walkthrough build's rules save: this browser only. The live app saves through
 *  saveAdminSettings('priorityRules', ...) (pages/Admin.jsx). */
export async function saveRules(rules) {
  const s = load();
  s.rules = clone(rules);
  save();
  return settle(clone(s.rules));
}

/** The walkthrough build's targets save: this browser only (live: saveAdminSettings('slaTargets', ...)). */
export async function saveSla(sla) {
  const s = load();
  s.sla = clone(sla);
  save();
  return settle(clone(s.sla));
}

/**
 * → POST /api/admin/rules/test
 * Replays a draft rule list over the tickets already raised and reports what
 * would change. The real version runs over 90 days of history and reports how
 * often IT overrode the result — which is what keeps rules maintained rather
 * than set once and forgotten.
 */
export async function testRules(rules) {
  const s = load();
  const changes = s.tickets.map((t) => {
    const facts = factsFor({
      // Seeded tickets predate the fault/request split, so derive the kind
      // rather than replaying them with it missing.
      ticket: { ...t, kind: t.kind || kindOf(t.requestTypeId) },
      requester: t.requester,
      similarCount: t.similarCount || 1,
      now: new Date(t.raisedAt),
    });
    const verdict = evaluate(rules, facts);
    const was = t.priorityOverride ? t.priorityOverride.to : t.priority;
    return {
      number: t.number,
      title: titleFor(t),
      was,
      wouldBe: verdict.priority,
      rule: verdict.ruleName,
      changed: verdict.priority !== was,
      overridden: Boolean(t.priorityOverride),
    };
  });
  return settle(changes);
}

// ----------------------------------------------------------------- helpers

/** 'fault' or 'request' for a type id — what decides which questions it asks. */
export function kindOf(requestTypeId) {
  return typeById(requestTypeId)?.kind || 'fault';
}

export function statusLabel(status) {
  return (
    { new: 'New', in_progress: 'In progress', waiting: 'Waiting', resolved: 'Resolved', closed: 'Closed' }[status] ||
    status
  );
}

/** What the queue and the lists show as the status, waiting reason included. */
export function statusText(ticket) {
  if (ticket.status !== 'waiting') return statusLabel(ticket.status);
  return ticket.waitingReason === 'third_party' ? 'On hold — supplier' : 'Waiting on requester';
}

/** Tickets have no free-text subject by design, so the title is composed. */
export function titleFor(ticket) {
  const type = typeById(ticket.requestTypeId);
  const cat = categoryById(ticket.categoryId);
  return type ? type.label : cat ? cat.label : 'Request';
}

export function staffName(id) {
  // A filed ticket's owner is a sign-in address (S06), shown as it is until names are known.
  if (typeof id === 'string' && id.includes('@')) return id;
  return IT_STAFF.find((p) => p.id === id)?.name || null;
}

/** "Marcus Webb" → "MW". Two letters, because that is all an avatar holds. */
export function initials(name) {
  return String(name || '?')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}
